import type { SearchResult, SearchResultSection } from '@amzn/das-finsuite-shared-ui';
import { buildEmbeddedAppPath } from 'src/utilities/urlName';
import type { ButtonDropdownProps } from '@amzn/awsui-components-core/button-dropdown';
import { WelcomeContentDetailsEntity } from 'src/components/das-finsuite/admin-components/AdminModels';
import { FinanceCalendarEventEntity } from 'src/api/interfaces';
import { AnnouncementFlatTableEntity, ApplicationFlatTableEntity, LogoConfig } from 'src/components/context/AppContextModels';
import type { NavItem } from 'src/components/side-navigation-v2/useNavigationItems';

// --- Search result types ---

export interface SearchResultItem {
  id: string;
  category: 'products' | 'announcements' | 'pages';
  title: string;
  description?: string;
  isFavorite?: boolean;
  href?: string;
  meta?: string;
  external?: boolean;
  /** Whether the application is embedded within FinSuite (products only) */
  embeddingEnabled?: boolean;
  /** The GL account / module category (products only) */
  glAccount?: string;
  /** Whether this is a flagship product (products only) */
  isFlagship?: boolean;
  /** Application logo configuration (products only) */
  logoConfig?: LogoConfig | null;
  /** Tool UI type e.g. 'Prophecy', 'Cloudscape (Polaris)' (products only) */
  toolUI?: string;
}

export const MAX_RESULTS_PER_CATEGORY = Infinity;

// --- Quick Links (side nav + profile dropdown) ---

export interface QuickLinkItem {
  id: string;
  text: string;
  href: string;
  external?: boolean;
  section?: string;
}

// --- Search logic ---

const matchesQuery = (text: string | undefined | null, query: string): boolean => {
  if (!text) return false;
  return text.toLowerCase().includes(query);
};

export const searchApplications = (apps: ApplicationFlatTableEntity[], query: string, favoriteIds: string[]): SearchResultItem[] => {
  return apps
    .filter(
      (app) => matchesQuery(app.applicationName, query) || matchesQuery(app.shortApplicationDescription, query) || matchesQuery(app.toolUI, query)
    )
    .sort((a, b) => {
      const aMatchesTitle = matchesQuery(a.applicationName, query) ? 0 : 1;
      const bMatchesTitle = matchesQuery(b.applicationName, query) ? 0 : 1;
      return aMatchesTitle - bMatchesTitle;
    })
    .map((app) => ({
      id: app.id,
      category: 'products' as const,
      title: app.applicationName,
      description: app.shortApplicationDescription,
      isFavorite: favoriteIds.includes(app.id),
      href: app.embeddingEnabled ? buildEmbeddedAppPath(app) : app.applicationLink || undefined,
      embeddingEnabled: app.embeddingEnabled,
      glAccount: app.glAccount,
      isFlagship: app.isFlagship,
      logoConfig: app.logoConfig,
      toolUI: app.toolUI
    }));
};

export const searchAnnouncements = (announcements: AnnouncementFlatTableEntity[], query: string): SearchResultItem[] => {
  return announcements
    .filter((a) => a.isActive && (matchesQuery(a.title, query) || matchesQuery(a.announcement, query) || matchesQuery(a.applicationName, query)))
    .sort((a, b) => {
      const aMatchesTitle = matchesQuery(a.title, query) ? 0 : 1;
      const bMatchesTitle = matchesQuery(b.title, query) ? 0 : 1;
      return aMatchesTitle - bMatchesTitle;
    })
    .map((a) => ({
      id: a.id,
      category: 'announcements' as const,
      title: a.title,
      description: a.applicationName,
      meta: a.announcementDate,
      href: a.announcementLink || undefined
    }));
};

export const searchQuickLinks = (links: QuickLinkItem[], query: string): SearchResultItem[] => {
  return links
    .filter((link) => matchesQuery(link.text, query) || matchesQuery(link.section, query))
    .sort((a, b) => {
      const aMatchesTitle = matchesQuery(a.text, query) ? 0 : 1;
      const bMatchesTitle = matchesQuery(b.text, query) ? 0 : 1;
      return aMatchesTitle - bMatchesTitle;
    })
    .map((link) => ({
      id: link.id,
      category: 'pages' as const,
      title: link.text,
      description: link.section,
      href: link.href,
      external: link.external
    }));
};

export const searchWelcomeContent = (items: WelcomeContentDetailsEntity[], query: string): SearchResultItem[] => {
  return items
    .filter((item) => item.displayText && (matchesQuery(item.displayText, query) || matchesQuery(item.description, query)))
    .sort((a, b) => {
      const aMatchesTitle = matchesQuery(a.displayText, query) ? 0 : 1;
      const bMatchesTitle = matchesQuery(b.displayText, query) ? 0 : 1;
      return aMatchesTitle - bMatchesTitle;
    })
    .map((item) => ({
      id: `welcome-${item.id || item.displayText}`,
      category: 'pages' as const,
      title: item.displayText!,
      description: item.description || undefined,
      href: item.link || undefined,
      external: !!item.link
    }));
};

export const searchFinanceCalendar = (items: FinanceCalendarEventEntity[], query: string): SearchResultItem[] => {
  return items
    .filter((item) => item.event && (matchesQuery(item.event, query) || matchesQuery(item.description, query) || matchesQuery(item.scenario, query)))
    .sort((a, b) => {
      const aMatchesTitle = matchesQuery(a.event, query) ? 0 : 1;
      const bMatchesTitle = matchesQuery(b.event, query) ? 0 : 1;
      return aMatchesTitle - bMatchesTitle;
    })
    .map((item) => ({
      id: `calendar-${item.event}`,
      category: 'pages' as const,
      title: item.event,
      description: item.description || undefined,
      meta: item.scenario
    }));
};

export interface GroupedSearchResults {
  products: SearchResultItem[];
  announcements: SearchResultItem[];
  pages: SearchResultItem[];
}

export const getAllApplicationsSortedByTab = (apps: ApplicationFlatTableEntity[], favoriteIds: string[]): SearchResultItem[] => {
  return apps
    .sort((a, b) => {
      const glCompare = (a.glAccount || 'Other').localeCompare(b.glAccount || 'Other');
      if (glCompare !== 0) return glCompare;
      return a.applicationName.localeCompare(b.applicationName);
    })
    .map((app) => ({
      id: app.id,
      category: 'products' as const,
      title: app.applicationName,
      description: app.shortApplicationDescription,
      isFavorite: favoriteIds.includes(app.id),
      href: app.embeddingEnabled ? buildEmbeddedAppPath(app) : app.applicationLink || undefined,
      embeddingEnabled: app.embeddingEnabled,
      glAccount: app.glAccount,
      isFlagship: app.isFlagship,
      logoConfig: app.logoConfig,
      toolUI: app.toolUI
    }));
};

export const performSearch = (
  query: string,
  apps: ApplicationFlatTableEntity[],
  announcements: AnnouncementFlatTableEntity[],
  favoriteIds: string[],
  quickLinks: QuickLinkItem[],
  welcomeContent: WelcomeContentDetailsEntity[],
  financeCalendar: FinanceCalendarEventEntity[]
): GroupedSearchResults => {
  const q = query.toLowerCase().trim();
  if (!q) {
    return {
      products: getAllApplicationsSortedByTab(apps, favoriteIds),
      announcements: [],
      pages: []
    };
  }

  return {
    products: searchApplications(apps, q, favoriteIds),
    announcements: searchAnnouncements(announcements, q),
    pages: [...searchQuickLinks(quickLinks, q), ...searchWelcomeContent(welcomeContent, q), ...searchFinanceCalendar(financeCalendar, q)]
  };
};

// ─── Functions moved from GlobalSearchBar.tsx ─────────────────────────────────

export const stripHtml = (html: string): string => html.replace(/<[^>]*>/g, '');

export const toSearchResult = (item: SearchResultItem): SearchResult => ({
  id: item.id,
  type: item.category,
  label: item.title,
  description: item.description ? stripHtml(item.description) : undefined,
  meta: item.meta,
  path: item.href,
  externalUrl: item.external ? item.href : undefined,
  isFavorite: item.isFavorite,
  embeddingEnabled: item.embeddingEnabled,
  glAccount: item.glAccount,
  isFlagship: item.isFlagship,
  logoConfig: item.logoConfig,
  toolUI: item.toolUI
});

export const buildSections = (results: GroupedSearchResults, filteredProducts: SearchResultItem[]): SearchResultSection[] => {
  const sections: SearchResultSection[] = [];
  if (filteredProducts.length > 0) {
    sections.push({ title: 'Products', results: filteredProducts.slice(0, MAX_RESULTS_PER_CATEGORY).map(toSearchResult) });
  }
  if (results.pages.length > 0) {
    sections.push({ title: 'Links', results: results.pages.slice(0, MAX_RESULTS_PER_CATEGORY).map(toSearchResult) });
  }
  if (results.announcements.length > 0) {
    sections.push({ title: 'Product Launches', results: results.announcements.slice(0, MAX_RESULTS_PER_CATEGORY).map(toSearchResult) });
  }
  return sections;
};

export interface ProductFilterTag {
  label: string;
  count: number;
}

export const buildProductFilterTags = (products: SearchResultItem[], moduleOrder: readonly string[]): ProductFilterTag[] => {
  const tags: ProductFilterTag[] = [{ label: 'All Products', count: products.length }];
  const flagshipCount = products.filter((p) => p.isFlagship).length;
  const favoritesCount = products.filter((p) => p.isFavorite).length;
  if (flagshipCount > 0) tags.push({ label: 'Flagship', count: flagshipCount });
  if (favoritesCount > 0) tags.push({ label: 'Favorites', count: favoritesCount });

  const counts: Record<string, number> = {};
  products.forEach((p) => {
    const module = p.glAccount && p.glAccount !== 'N/A' ? p.glAccount : 'Other';
    counts[module] = (counts[module] || 0) + 1;
  });
  const ordered = moduleOrder.filter((m) => counts[m]);
  const unlisted = Object.keys(counts)
    .filter((m) => !moduleOrder.includes(m))
    .sort((a, b) => a.localeCompare(b));
  [...ordered, ...unlisted].forEach((label) => tags.push({ label, count: counts[label] }));
  return tags;
};

export const buildQuickLinks = (navItems: NavItem[], profileDropdownItems: ReadonlyArray<ButtonDropdownProps.ItemOrGroup>): QuickLinkItem[] => {
  const links: QuickLinkItem[] = [];
  let currentSection = '';

  navItems.forEach((item) => {
    if (item.type === 'section-header') {
      currentSection = item.text;
    } else if (item.type === 'link' && item.href) {
      links.push({ id: `nav-${item.href}`, text: item.text, href: item.href, external: item.external, section: currentSection || 'Navigation' });
    }
  });

  profileDropdownItems.forEach((group) => {
    if ('items' in group && group.items) {
      group.items.forEach((item) => {
        if ('href' in item && item.href && item.text) {
          links.push({ id: `profile-${item.id}`, text: item.text, href: item.href, external: true, section: 'Quick Links' });
        }
      });
    } else if ('href' in group && group.href && group.text) {
      links.push({ id: `profile-${group.id}`, text: group.text, href: group.href, external: true, section: 'Quick Links' });
    }
  });

  return links;
};
